import { canGameAction, newGameRequestId, uuidPattern, validGameState, positiveAmount, gameId } from './gameplay.mjs';
import { gameplayDrawStorageKey } from './gameplayRecovery.mjs';
import { challengeRevisionPattern } from './challengeStages.mjs';
import { validPredictionPending } from './predictionSeries.mjs';

// Shared across remounts. Persist the UUID and, for rain or multiple tiers, its public target key; never tokens or reward data.
const operations = new Map();
export function createGameplaySession({ api, identity, token, storage, requestId = newGameRequestId, changed = () => {}, now = Date.now, withLock }) {
  let id, type, scope, openedToken, alive = true, sequence = 0, requestSequence = 0;
  let value = { state: null, loading: false, busy: false, error: '', actionError: '', message: '', pendingDraw: '', pendingDrawTarget: '', pendingTargetKey: '', buffPending: null, challengePending: null, predictionPending: null, lastPredictionReceipt: null };
  const publish = patch => { value = { ...value, ...patch }; changed(value); };
  const key = () => gameplayDrawStorageKey(scope, id);
  const operationKey = () => `${scope}:${id}`;
  const current = (version, actor, sessionToken) => alive && sequence === version && scope === actor && identity() === actor && token() === sessionToken;
  function readPending() {
    const saved = storage.get(key());
    if (!saved) return '';
    if (typeof saved !== 'string' || !uuidPattern.test(saved)) throw new Error('上次抽取记录无法读取，请联系客服核对');
    return saved;
  }
  function pendingTierTarget(pendingDraw) {
    if (!['POINT_DRAW', 'STEP_CHALLENGE'].includes(type) || !pendingDraw) return '';
    const original = storage.get(`${key()}:tier`);
    return original?.requestId === pendingDraw ? original.targetKey : '';
  }
  function readChallengePending() {
    if (type !== 'CHALLENGE') return null;
    const saved = storage.get(`${key()}:challenge`) || null;
    if (saved && (!uuidPattern.test(saved.requestId) || !challengeRevisionPattern.test(saved.ruleRevision) ||
        !['CLAIM', 'SYNC_CHALLENGE_TASKS'].includes(saved.action) ||
        (saved.action === 'SYNC_CHALLENGE_TASKS' ? saved.targetKey !== 'tasks' : !/^stage-[1-9]\d*-reward-[1-9]\d*$/.test(saved.targetKey)))) throw new Error('上次闯关结果待核对，请保留原请求');
    return saved;
  }
  // 原竞猜未确认前，只能查询或重试同一动作、玩法、选项和UUID。
  function readPredictionPending() {
    if (type !== 'PREDICTION') return null;
    const saved = storage.get(`${key()}:prediction`) || null;
    if (saved && !validPredictionPending(saved)) throw new Error('原竞猜请求待核对，请保留原编号');
    return saved;
  }
  async function refresh({ silent = false, clearActionError = false } = {}) {
    if (alive && (identity() !== scope || token() !== openedToken)) return open(id, type);
    if (!alive || !scope || !token()) return;
    const version = sequence, actor = scope, sessionToken = token(), request = ++requestSequence;
    if (!silent || !value.state) publish({ loading: true, error: '' });
    try {
      const state = await api.state(id);
      if (!current(version, actor, sessionToken) || request !== requestSequence) return;
      if (!validGameState(state, id, type)) throw new Error('活动状态暂不可用，请重新查询');
      const pendingDraw = ['CARD_COLLECTION', 'THEME_DRAW', 'POINT_DRAW', 'DEPOSIT_DRAW', 'STEP_CHALLENGE', 'RED_PACKET_RAIN'].includes(type) ? readPending() : '';
      let pendingDrawTarget = '';
      if (pendingDraw && type === 'DEPOSIT_DRAW' && state.rules.depositDrawPlays?.length) {
        const original = storage.get(`${key()}:play`);
        if (!original || original.requestId !== pendingDraw || !state.rules.depositDrawPlays.some(play => play.key === original.playKey)) throw new Error('原抽奖玩法暂无法核对，请保留请求编号');
        pendingDrawTarget = original.playKey;
      }
      const originalTier = ['POINT_DRAW', 'STEP_CHALLENGE'].includes(type) && pendingDraw ? storage.get(`${key()}:tier`) : null;
      if (pendingDraw && state.rules?.pointDraw?.tiers?.length && (!originalTier || originalTier.requestId !== pendingDraw || !/^gear_[1-9][0-9]*$/.test(originalTier.targetKey))) throw new Error('原射门档位暂无法核对，请保留请求编号');
      if (pendingDraw && state.rules?.legacyChallenge?.stages?.length && (!originalTier || originalTier.requestId !== pendingDraw || !/^legacy-[a-f0-9]{24}$/.test(originalTier.targetKey))) throw new Error('原转盘关卡暂无法核对，请保留请求编号');
      const buffPending = type === 'BUFF_CARD' ? storage.get(`${key()}:buff`) || null : null;
      if (buffPending && (!uuidPattern.test(buffPending.requestId) || !['CLAIM','DRAW','UPGRADE'].includes(buffPending.action) || typeof buffPending.targetKey !== 'string')) throw new Error('原领卡请求待核对，请保留原记录');
      let challengePending = readChallengePending();
      if (challengePending && state.challenge?.confirmedRequestIds?.includes(challengePending.requestId)) {
        if (storage.get(`${key()}:challenge`)?.requestId === challengePending.requestId) storage.remove(`${key()}:challenge`);
        challengePending = readChallengePending();
      }
      const predictionPending = readPredictionPending();
      if (predictionPending && !state.rules.predictionSeries) throw new Error('原竞猜规则待核对，请保留原请求');
      publish({ state, buffPending, challengePending, predictionPending, pendingDraw, pendingDrawTarget, pendingTargetKey: originalTier?.requestId === pendingDraw ? originalTier.targetKey : '', ...(clearActionError ? { actionError: '' } : {}) });
    } catch (error) {
      if (current(version, actor, sessionToken) && request === requestSequence) publish({ error: error.message || '活动加载失败，请重试' });
    } finally {
      if (current(version, actor, sessionToken) && request === requestSequence) publish({ loading: false });
    }
  }
  async function open(activityId, activityType) {
    alive = true; sequence++; requestSequence++;
    id = activityId; type = activityType; scope = identity(); openedToken = token();
    value = { state: null, loading: false, busy: false, error: '', actionError: '', message: '', pendingDraw: '', pendingDrawTarget: '', pendingTargetKey: '', buffPending: null, challengePending: null, predictionPending: null, lastPredictionReceipt: null };
    publish({});
    if (!scope || !token()) { publish({ error: '请登录后查看活动进度' }); return; }
    await refresh();
  }
  async function executeInternal(action, targetKey = '', optionKey = '') {
    if (!alive || value.loading || value.busy || value.error || !token() || identity() !== scope) return;
    const retryDraw = action === 'DRAW' && uuidPattern.test(value.pendingDraw) && ['CARD_COLLECTION', 'THEME_DRAW', 'POINT_DRAW', 'DEPOSIT_DRAW', 'STEP_CHALLENGE', 'RED_PACKET_RAIN'].includes(type);
    const buffAction = type === 'BUFF_CARD' && Boolean(value.state?.rules?.buffCard?.collection);
    const buffRetry = buffAction && value.buffPending?.action === action && value.buffPending?.targetKey === targetKey;
    const challengeAction = type === 'CHALLENGE' && Boolean(value.state?.rules?.challenge?.stages?.length);
    const challengeOriginal = challengeAction ? readChallengePending() : null;
    const challengeRetry = challengeAction && challengeOriginal?.action === action && challengeOriginal?.targetKey === targetKey;
    if (challengeOriginal && !challengeRetry) { publish({ actionError: '请先核对上次闯关操作' }); return; }
    if (buffAction && value.buffPending && !buffRetry) { publish({ actionError: '请先核对上一笔卡片操作' }); return; }
    const predictionAction = type === 'PREDICTION' && Boolean(value.state?.rules?.predictionSeries);
    const predictionOriginal = predictionAction ? readPredictionPending() : null;
    const predictionRetry = predictionOriginal?.action === action && predictionOriginal?.targetKey === targetKey && predictionOriginal?.optionKey === optionKey;
    if (predictionOriginal && !predictionRetry) { publish({ actionError: '请先核对上一笔竞猜原请求' }); return; }
    if (!retryDraw && !buffRetry && !challengeRetry && !predictionRetry && !canGameAction(value.state, action, targetKey, optionKey, now())) return;
    const lock = operationKey();
    if (operations.has(lock)) { publish({ actionError: '上一笔操作正在核对，请稍后刷新进度' }); return; }
    const version = sequence, actor = scope, sessionToken = token(), activityId = id, activityType = type;
    const operation = {}; operations.set(lock, operation);
    publish({ busy: true, actionError: '', message: '' });
    try {
      const data = { action, ...(targetKey ? { targetKey } : {}), ...(optionKey ? { optionKey } : {}) };
      if (predictionAction) {
        const original = readPredictionPending();
        if (original && (original.action !== action || original.targetKey !== targetKey || original.optionKey !== optionKey)) throw new Error('原竞猜请求不可更改');
        data.requestId = original?.requestId || requestId();
        const predictionPending = { requestId: data.requestId, action, targetKey, optionKey };
        if (!validPredictionPending(predictionPending)) throw new Error('无法生成有效竞猜请求编号');
        storage.set(`${key()}:prediction`, predictionPending);
        publish({ predictionPending });
      }
      if (challengeAction) {
        const retained = readChallengePending();
        if (retained && (retained.action !== action || retained.targetKey !== targetKey)) throw new Error('原闯关请求不可更改');
        data.requestId = retained?.requestId || requestId();
        data.ruleRevision = retained?.ruleRevision || value.state.challenge.revision;
        if (!uuidPattern.test(data.requestId) || !challengeRevisionPattern.test(data.ruleRevision)) throw new Error('无法核对闯关请求版本');
        const challengePending = { requestId: data.requestId, ruleRevision: data.ruleRevision, action, targetKey };
        storage.set(`${key()}:challenge`, challengePending); publish({ challengePending });
      }
      if (buffAction) {
        const original = storage.get(`${key()}:buff`);
        if (value.buffPending && original && original.requestId !== value.buffPending.requestId) throw new Error('待核对卡片记录已变化，请刷新进度');
        const retained = value.buffPending || original;
        if (retained && (retained.action !== action || retained.targetKey !== targetKey)) throw new Error('原卡片请求不可更改');
        data.requestId = retained?.requestId || requestId();
        if (!uuidPattern.test(data.requestId)) throw new Error('无法生成有效请求编号');
        const buffPending = { requestId: data.requestId, action, targetKey };
        storage.set(`${key()}:buff`, buffPending); publish({ buffPending });
      }
      if (action === 'DRAW' && !buffAction) {
        const existing = readPending();
        // A retry remains bound to the UUID the user is confirming, even if a
        // different tab has already succeeded and removed its storage entry.
        if (value.pendingDraw && existing && existing !== value.pendingDraw) throw new Error('待核对记录已变化，请刷新进度');
        data.requestId = value.pendingDraw || existing || requestId();
        if (!uuidPattern.test(data.requestId)) throw new Error('无法生成有效请求编号');
        if (activityType === 'RED_PACKET_RAIN') {
          const original = storage.get(`${key()}:round`);
          if (value.pendingDraw || existing) {
            if (!original || original.requestId !== data.requestId || !uuidPattern.test(original.roundKey)) throw new Error('原红包场次暂无法核对，请保留请求编号');
            data.targetKey = original.roundKey;
          } else {
            if (!uuidPattern.test(targetKey)) throw new Error('红包场次编号无效');
            storage.set(`${key()}:round`, { requestId: data.requestId, roundKey: targetKey });
          }
        }
        if (activityType === 'DEPOSIT_DRAW' && value.state.rules.depositDrawPlays?.length) {
          const original = storage.get(`${key()}:play`);
          if (value.pendingDraw || existing) {
            if (!original || original.requestId !== data.requestId || !value.state.rules.depositDrawPlays.some(play => play.key === original.playKey)) throw new Error('原抽奖玩法暂无法核对，请保留请求编号');
            data.targetKey = original.playKey;
          } else {
            storage.set(`${key()}:play`, { requestId: data.requestId, playKey: targetKey });
          }
        }
        if (['POINT_DRAW', 'STEP_CHALLENGE'].includes(activityType)) {
          const multiple = activityType === 'POINT_DRAW' ? value.state.rules?.pointDraw?.tiers?.length : value.state.rules?.legacyChallenge?.stages?.length;
          const targetPattern = activityType === 'POINT_DRAW' ? /^gear_[1-9][0-9]*$/ : /^legacy-[a-f0-9]{24}$/;
          const original = storage.get(`${key()}:tier`);
          if (value.pendingDraw || existing) {
            if (multiple && (!original || original.requestId !== data.requestId || !targetPattern.test(original.targetKey))) throw new Error('原参与档位或关卡暂无法核对，请保留请求编号');
            data.targetKey = original?.requestId === data.requestId ? original.targetKey : '';
          } else if (targetKey) {
            if (!targetPattern.test(targetKey)) throw new Error('参与档位或关卡编号无效');
            storage.set(`${key()}:tier`, { requestId: data.requestId, targetKey });
          }
        }
        storage.set(key(), data.requestId); // Must succeed before sending a non-repeatable action.
        publish({ pendingDraw: data.requestId, pendingDrawTarget: activityType === 'DEPOSIT_DRAW' ? data.targetKey || '' : '', pendingTargetKey: ['POINT_DRAW', 'STEP_CHALLENGE'].includes(activityType) ? data.targetKey || '' : '' });
      }
      let result;
      if (buffRetry && typeof api.recover === 'function') {
        try {
          const original = await api.recover(activityId, data.requestId);
          if (gameId(original?.activityId) !== gameId(activityId) || original.requestId !== data.requestId || original.action !== action || original.targetKey !== targetKey || !gameId(original.recordId) || original.status !== 'COMPLETED') throw new Error('原卡片操作暂无法核对，请保留请求编号');
          result = { ...original, state: await api.state(activityId) };
        } catch (error) { if (error.errorCode !== 'GAMEPLAY_RECORD_NOT_FOUND') throw error; }
      }
      if (predictionRetry && typeof api.recover === 'function') {
        try {
          const original = await api.recover(activityId, data.requestId);
          if (gameId(original?.activityId) !== gameId(activityId) || original.requestId !== data.requestId || original.action !== action || original.targetKey !== targetKey || action === 'PICK' && original.optionKey !== optionKey || !gameId(original.recordId) || !['PENDING', 'COMPLETED'].includes(original.status)) throw new Error('原竞猜结果暂无法核对，请保留请求编号');
          result = { ...original, state: await api.state(activityId) };
        } catch (error) { if (error.errorCode !== 'GAMEPLAY_RECORD_NOT_FOUND') throw error; }
      }
      if (!result) result = await api.action(activityId, data);
      if (!current(version, actor, sessionToken)) return;
      if (!gameId(result?.recordId) || !['PENDING', 'COMPLETED'].includes(result.status) || !validGameState(result.state, activityId, activityType)) throw new Error('操作结果待核对，请刷新进度');
      if (challengeAction && action === 'CLAIM' && result.status === 'PENDING') {
        const original = result.state.challenge.stages.flatMap(stage => stage.rewards).find(reward => reward.key === targetKey && String(reward.recordId) === String(result.recordId));
        if (!original?.reserved || original.claimed || !original.bonusOrderId || result.rewardAmount !== '0.00') throw new Error('原场馆关奖待核对，请刷新进度');
      }
      if (predictionAction) {
        if (result.targetKey !== targetKey || action === 'PICK' && result.optionKey !== optionKey) throw new Error('原竞猜目标或选项不一致，请保留请求');
        const originalRequestId = result.requestId || data.requestId;
        if (!uuidPattern.test(originalRequestId) || action === 'PICK' && originalRequestId !== data.requestId) throw new Error('原竞猜请求编号不一致，请保留原请求');
        if (storage.get(`${key()}:prediction`)?.requestId === data.requestId) storage.remove(`${key()}:prediction`);
        publish({ predictionPending: readPredictionPending(), lastPredictionReceipt: { requestId: originalRequestId, recordId: gameId(result.recordId), action, targetKey, optionKey: result.optionKey, status: result.status, rewardAmount: result.rewardAmount } });
      }
      if (buffAction && storage.get(`${key()}:buff`)?.requestId === data.requestId) { storage.remove(`${key()}:buff`); publish({ buffPending: null }); }
      if (challengeAction && storage.get(`${key()}:challenge`)?.requestId === data.requestId) { storage.remove(`${key()}:challenge`); publish({ challengePending: null }); }
      if (action === 'DRAW' && !buffAction) {
        // Another tab may have changed the pending key; only clear this exact receipt.
        if (storage.get(key()) === data.requestId) {
          storage.remove(key());
          // Keep the public rain binding for an already-open tab retrying this UUID.
        }
        const pendingDraw = readPending();
        publish({ pendingDraw, pendingDrawTarget: '', pendingTargetKey: pendingTierTarget(pendingDraw) });
      }
      ++requestSequence; // A pre-action GET must never overwrite this confirmed result.
      const drawn = action === 'DRAW' ? [...(result.state.rules.cards || []), ...(result.state.rules.prizes || [])].find(item => item.key === result.optionKey) : null;
      const outcome = drawn?.title ? `本次抽中：${drawn.title}` : '操作已完成';
      let message = activityType === 'MONTHLY_ENTRY' ? '本月报名已登记，可在申请记录中查看；本项不发放奖励' : activityType === 'BUFF_CARD' ? '卡片领取成功，可在“我的卡片”查看有效期' : result.status === 'PENDING' ? '选择已提交，等待结果公布' : positiveAmount(result.rewardAmount) ? `${outcome}，奖励 ${result.rewardAmount} 元，结果已确认` : `${outcome}，请查看最新进度`;
      if (buffAction) message = `${action === 'UPGRADE' ? '升级' : '领卡'}成功：${result.targetTitle || '卡片'}${action === 'UPGRADE' ? '，原卡已消耗' : '，可在“我的卡片”查看有效期'}`;
      if (challengeAction && action === 'SYNC_CHALLENGE_TASKS') message = '闯关任务已核对，体力和任务红利请查看最新进度';
      if (challengeAction && action === 'CLAIM' && result.status === 'PENDING') message = '关卡奖励已保留，请选择场馆领取并核对到账';
      if (activityType === 'POINT_DRAW' && action === 'DRAW') {
        const award = Number.isSafeInteger(result.rewardPoints) && result.rewardPoints > 0 ? `奖励 ${result.rewardPoints} 积分已到账` : positiveAmount(result.rewardAmount) ? `奖励 ${result.rewardAmount} 元已到账` : '本次未中奖';
        message = `${result.targetTitle || '射门'}结果已确认，${award}，同一请求不会重新抽取`;
      }
      if (activityType === 'RED_PACKET_RAIN' && action === 'DRAW') {
        const summary = positiveAmount(result.rewardAmount) ? `红包中奖 ${result.rewardAmount} 元${result.state.redPacketRain?.bonusOrderId ? '，请在原单选择场馆领取彩金' : '已到账'}` : result.optionKey === '该档已抢完' ? '该档已抢完，本次未发放奖金' : '本次未中奖，未发放奖金';
        message = `${summary}，已使用1次参与机会；同一请求不会重新抽取`;
      }
      if (activityType === 'STEP_CHALLENGE' && action === 'DRAW') {
        const root = result.state.legacyChallenge;
        const entry = root.stages?.find(stage => stage.key === result.targetKey) || root;
        const rule = result.state.rules.legacyChallenge.stages?.find(stage => stage.key === result.targetKey) || result.state.rules.legacyChallenge;
        const sameReceipt = gameId(entry.recordId) === gameId(result.recordId);
        const step = entry.lastStep > 0 ? `前进${entry.lastStep}步` : entry.lastStep < 0 ? `后退${Math.abs(entry.lastStep)}步` : '原地不动';
        const progress = `当前位置 ${entry.position}/${rule.totalSteps}`;
        const award = Number.isSafeInteger(result.rewardPoints) && result.rewardPoints > 0 ? `格子奖励 ${result.rewardPoints} 积分已到账` : positiveAmount(result.rewardAmount) ? `${rule.prizes?.length ? '格子奖励' : '终点奖励'} ${result.rewardAmount} 元已到账` : '本次没有发放奖金';
        message = `${result.targetTitle || rule.title || '旧闯关'}：${sameReceipt ? `本次${step}，已使用1次转盘机会` : '原转盘结果已确认'}，${progress}；${award}`;
      }
      publish({ state: result.state, loading: false, error: '', message });
    } catch (error) {
      if (current(version, actor, sessionToken)) {
        if (predictionAction && error.errorCode === 'PREDICTION_NOT_COMMITTED') {
          const original = readPredictionPending();
          if (original?.requestId === value.predictionPending?.requestId) storage.remove(`${key()}:prediction`);
          publish({ predictionPending: readPredictionPending() });
        }
        if (challengeAction && error.errorCode === 'CHALLENGE_NOT_COMMITTED') {
          const original = readChallengePending();
          if (original?.requestId === value.challengePending?.requestId) storage.remove(`${key()}:challenge`);
          publish({ challengePending: readChallengePending() });
        }
        // 仅事务明确确认未产生原记录时结束该UUID；网络未知仍保留恢复。
        // A timeout, missing lookup or generic rejection keeps it for original-result recovery.
        if (action === 'DRAW' && ((activityType === 'RED_PACKET_RAIN' && error.errorCode === 'RAIN_DRAW_NOT_COMMITTED') || (activityType === 'DEPOSIT_DRAW' && error.errorCode === 'DEPOSIT_DRAW_NOT_COMMITTED'))) {
          if (storage.get(key()) === value.pendingDraw) storage.remove(key());
          const pendingDraw = readPending();
        publish({ pendingDraw, pendingDrawTarget: '', pendingTargetKey: pendingTierTarget(pendingDraw) });
        }
        publish({ actionError: error.message || '结果待核对，请刷新进度后重试' });
        await refresh();
      }
    } finally {
      if (operations.get(lock) === operation) operations.delete(lock);
      if (current(version, actor, sessionToken)) publish({ busy: false });
    }
  }
  async function applyPrizeInternal(entitlement, receipt = {}) {
    if (!alive || value.loading || value.busy || value.error || identity() !== scope || !token()) return;
    const item = value.state?.entitlements?.find(row => String(row.recordId) === String(entitlement?.recordId) && String(row.prizeId) === String(entitlement?.prizeId));
    if (!item) return;
    if (gameId(item.prizeOrderId)) {
      publish({ message: '该奖品已有申请，可查看进度' });
      return { id: item.prizeOrderId };
    }
    const lock = operationKey();
    if (operations.has(lock)) { publish({ actionError: '上一笔操作正在核对，请稍后刷新进度' }); return; }
    const version = sequence, actor = scope, sessionToken = token(), activityId = id, operation = {};
    operations.set(lock, operation); publish({ busy: true, actionError: '', message: '' });
    try {
      const shipping = Object.fromEntries(['recipientName', 'recipientPhone', 'shippingAddress'].filter(key => receipt[key] !== undefined).map(key => [key, String(receipt[key]).trim()]));
      const result = await api.applyPrize(activityId, { prizeId: item.prizeId, gameRecordId: item.recordId, ...shipping });
      if (!current(version, actor, sessionToken)) return;
      if (!gameId(result?.id)) throw new Error('申请结果待核对，请刷新进度');
      publish({ message: '奖品申请已提交，等待审核' });
      await refresh();
      return current(version, actor, sessionToken) ? result : undefined;
    } catch (error) {
      if (current(version, actor, sessionToken)) {
        publish({ actionError: error.message || '申请结果待核对，请刷新进度' }); await refresh();
        const recovered = value.state?.entitlements?.find(row => String(row.recordId) === String(item.recordId));
        if (current(version, actor, sessionToken) && !value.error && gameId(recovered?.prizeOrderId)) { publish({ actionError: '', message: '奖品申请已确认，可查看进度' }); return { id: recovered.prizeOrderId }; }
      }
    } finally {
      if (operations.get(lock) === operation) operations.delete(lock);
      if (current(version, actor, sessionToken)) publish({ busy: false });
    }
  }
  async function locked(work) {
    if (!alive) return;
    if (identity() !== scope || token() !== openedToken) return open(id, type);
    if (!scope || !token()) return;
    if (!withLock) return work();
    const version = sequence, actor = scope, sessionToken = token();
    return withLock(`kg-gameplay:${operationKey()}`, async acquired => {
      if (!current(version, actor, sessionToken)) return;
      if (!acquired) { publish({ actionError: '其他页面正在核对本活动，请稍后刷新进度' }); return; }
      return work();
    }).catch(error => { if (current(version, actor, sessionToken)) publish({ actionError: error.message || '暂时无法核对操作，请稍后重试' }); });
  }
  const execute = (action, key, option) => locked(() => executeInternal(action, key, option));
  const applyPrize = (entitlement, receipt) => locked(() => applyPrizeInternal(entitlement, receipt));
  function pause() { alive = false; sequence++; requestSequence++; publish({ state: null, loading: false, busy: false, message: '', actionError: '', pendingDraw: '', pendingDrawTarget: '', pendingTargetKey: '', buffPending: null, challengePending: null, predictionPending: null, lastPredictionReceipt: null }); }
  const clearFeedback = () => publish({ message: '', actionError: '' });
  return { open, refresh, execute, applyPrize, pause, clearFeedback, get value() { return value; } };
}
